class LoadMore extends HTMLElement {
  constructor() {
    super();
    const { loading, loadMore } = window.accessibilityStrings || {};
    this.loadingText = loading || 'Loading...';
    this.loadMoreText = loadMore || 'Load more';

    // Elements
    this.button = this.querySelector('.load-more');
    this.statusEl = this.querySelector('.load-more__status');
    this.statusTextEl = this.querySelector('.load-more__status-text');
    this.statusFormat = this.statusTextEl?.getAttribute('data-format') || null;
    // Allow specifying grid via attribute fallback to first [data-grid]
    const gridSelector = this.getAttribute('data-grid-selector') || '[data-grid]';
    this.gridElement = document.querySelector(gridSelector);

  this.totalItems = parseInt(this.getAttribute('data-total'), 10) || null;
  this.initialCount = parseInt(this.getAttribute('data-initial-count'), 10) || null;

    if (!this.button) return; // Nothing to wire up

    this.onClick = this.onClick.bind(this);
    this.button.addEventListener('click', this.onClick);
  }

  get nextUrl() {
    return this.button?.dataset.nextUrl;
  }

  set nextUrl(value) {
    if (this.button) {
      if (value) {
        this.button.dataset.nextUrl = value;
      } else {
        delete this.button.dataset.nextUrl;
      }
    }
  }

  setButtonStateLoading() {
    if (this.button) {
      this.originalButtonText = this.button.textContent;
      this.button.textContent = this.loadingText;
      this.button.disabled = true;
      this.button.setAttribute('aria-busy', 'true');
      this.setAttribute('aria-busy', 'true');
      if (this.statusEl) {
        this.statusEl.setAttribute('data-loading', 'true');
      }
    }
  }

  resetButtonState() {
    if (this.button) {
      this.button.textContent = this.loadMoreText;
      this.button.disabled = false;
      this.button.removeAttribute('aria-busy');
      this.removeAttribute('aria-busy');
      if (this.statusEl) {
        this.statusEl.removeAttribute('data-loading');
      }
    }
  }

  removeButton() {
    if (!this.button) return;
    this.button.remove();
    this.button = null;
    if (this.statusEl) {
      this.statusEl.remove();
      this.statusEl = null;
      this.statusTextEl = null;
    }
  }

  async onClick() {
    if (!this.nextUrl || !this.gridElement) return;
    this.setButtonStateLoading();

    try {
      await this.fetchAndAppend(this.nextUrl);
    } catch (e) {
      console.error('[LoadMore] fetch failed', e);
      this.resetButtonState();
    }
  }

  async fetchAndAppend(url) {
    const response = await fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
    if (!response.ok) throw new Error(`Response status: ${response.status}`);

    const html = await response.text();
    const parser = new DOMParser();
    const newDocument = parser.parseFromString(html, 'text/html');
    const newGrid = newDocument.querySelector('[data-grid]');

    if (newGrid && this.gridElement) {
  // Capture elements with data-aos before insertion to identify just-added nodes
  const tempWrapper = document.createElement('div');
  tempWrapper.innerHTML = newGrid.innerHTML;
  const newAosNodes = Array.from(tempWrapper.querySelectorAll('[data-aos]'));
  this.gridElement.insertAdjacentHTML('beforeend', tempWrapper.innerHTML);
  this.refreshAOS(newAosNodes);
  this.updateStatus();
    }

    const newNextUrl = newDocument.querySelector('[data-load-more]')?.dataset.nextUrl;
    if (newNextUrl) {
      this.nextUrl = newNextUrl;
      this.resetButtonState();
    } else {
      this.removeButton();
    }
  }

  refreshAOS(newAosNodes = []) {
    // If AOS library present, refresh so it picks up new nodes
    if (window.AOS) {
      if (typeof window.AOS.refreshHard === 'function') {
        window.AOS.refreshHard();
      } else if (typeof window.AOS.refresh === 'function') {
        window.AOS.refresh();
      }

      // Manually animate any new nodes already in viewport (sometimes AOS won't trigger immediately for dynamically injected content already visible)
      requestAnimationFrame(() => {
        newAosNodes.forEach(el => {
          // Skip if library already applied animation
            if (el.classList.contains('aos-animate')) return;
            const rect = el.getBoundingClientRect();
            if (rect.top < window.innerHeight && rect.bottom > 0) {
              el.classList.add('aos-animate');
            }
        });
      });
    }
  }

  getVisibleCount() {
    if (!this.gridElement) return 0;
    // Assuming each product card is a direct child li
    return this.gridElement.children.length;
  }

  updateStatus() {
    if (!this.statusTextEl || !this.totalItems) return;
    const visible = this.getVisibleCount();
    if (this.statusFormat) {
      const text = this.statusFormat.replace('((current))', visible).replace('((total))', this.totalItems);
      this.statusTextEl.textContent = text;
    } else {
      // Fallback (untranslated if format missing)
      this.statusTextEl.textContent = `Showing ${visible} of ${this.totalItems}`;
    }
  }
}

customElements.define('load-more', LoadMore);